<script setup>
/**
 * Useful to display a button in a component.
 * The button will show the label unless the slot is filled.
 *
 * Reacts to the processing and recentlySuccessful state of a form.
 * If the form is processing, the button will show a spinner.
 * If the form was recently successful, the button will show a checkmark.
 *
 * Can be used as a link or a button, by setting the is prop to 'a' or 'button'.
 */
import { Link } from '@inertiajs/vue3';
import { computed, useSlots } from 'vue';
import ReactiveSlot from '@/Components/Button/Partials/ReactiveSlot.vue';

const props = defineProps({
    label: String,
    href: {
        type: String,
        default: '',
    },

    form: Object,

    is: String,
    disabled: Boolean,

    red: Boolean,
    transparent: Boolean,

    classes: String,
    fullWidth: Boolean,
    small: Boolean,
    white: Boolean,
    external: Boolean,
});

const normal = !props.red && !props.transparent;

const slots = useSlots();

const hasError = computed(() => {
    return typeof props.form?.errors == 'undefined'
        ? false
        : Object.values(props.form.errors).some((error) => error.length > 0);
});

const is = computed(() => {
    if (props.external) {
        return 'a';
    }

    if (props.is == 'button') {
        return 'button';
    }

    if (props.href) {
        return Link;
    }

    return 'span';
});
</script>

<template>
    <component
        :is="is"
        :class="[
            {
                'bg-red-700 hover:bg-red-800 focus:ring-red-600': red || hasError,
                'focus:secondary bg-primary px-4 py-6 text-primary-text hover:bg-primary-hover':
                    normal && !hasError && !small,
                'border-gray-300 bg-white text-gray-700': transparent && !hasError,
                'cursor-progress': form?.processing,
                'cursor-not-allowed': form?.recentlySuccessful,
                'md:w-64': !fullWidth,
                'bg-white hover:bg-white/70': white,
                'focus:secondary w-20 bg-primary py-2 hover:bg-secondary': small,
            },
            classes,
        ]"
        :disabled="disabled"
        :href="href != '' ? href : null"
        :style="[
            {
                'border-radius': 'var(--button-border-radius)',
                'font-size': 'var(--button-font-size)',
                'font-weight': 'var(--button-font-weight)',
                'text-transform': 'var(--button-text-transform)',
            },
        ]"
        :target="external ? '_blank' : ''"
        :type="is == 'button' ? 'button' : ''"
        class="relative inline-flex h-10 w-full items-center border border-transparent shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:bg-gray-300 disabled:text-gray-700"
    >
        <ReactiveSlot
            :processing="form?.processing ?? false"
            :recently-successful="form?.recentlySuccessful ?? false"
        >
            <div
                v-if="!slots.default"
                class="text-center leading-tight"
            >
                {{ label }}
            </div>

            <slot />
        </ReactiveSlot>
    </component>
</template>
